'use client';

/**
 * VendorResponseChat - Chat wrapper for vendor responses
 *
 * Fetches the vendor response data and delegates to VendorChat
 * for messaging the vendor who submitted this quote.
 *
 * @component_role VendorResponse Chat Wrapper
 * @business_rule Vendor response chat routes to the vendor's contact list
 *   for WhatsApp messaging. Uses the vendor linked to the response via
 *   LINKED_TO {type: 'vendor'} relationship.
 * @user_outcome Message the vendor about their quote - ask for clarification,
 *   negotiate pricing, or confirm order details.
 * @integration_point klyde_edge /neo/graph/explore/vendor-response
 *
 * @layer presentation
 * @arch_component_type wrapper
 * @arch_domain copilot
 */

import { Loader2, MessageSquare } from 'lucide-react';
import { useVendorResponseExplore } from '@/lib/api/hooks';
import { useCapabilityStore } from '@/lib/stores/capability-store';
import { VendorChat } from './vendor-chat';

interface VendorResponseChatProps {
  responseCdk: string;
  projectId?: string | null;
}

export function VendorResponseChat({ responseCdk, projectId }: VendorResponseChatProps) {
  const vendorResponseStrategy = useCapabilityStore((s) => s.strategyName('graph_explore', 'vendor_response'));
  const { data, isLoading, error } = useVendorResponseExplore(vendorResponseStrategy!, { response_cdk: responseCdk });

  // Loading state
  if (isLoading) {
    return (
      <div className="flex flex-col items-center justify-center h-full text-center text-muted-foreground">
        <Loader2 className="h-8 w-8 mb-2 animate-spin" />
        <p className="text-sm">Loading response details...</p>
      </div>
    );
  }

  // Error or no data
  if (error || !data?.response) {
    return (
      <div className="flex flex-col items-center justify-center h-full text-center text-muted-foreground">
        <MessageSquare className="h-8 w-8 mb-2 opacity-50" />
        <p className="text-sm">Could not load response</p>
        <p className="text-xs mt-1">Unable to find vendor contact information</p>
      </div>
    );
  }

  // No vendor linked
  if (!data.response.vendor?.semantic_id) {
    return (
      <div className="flex flex-col items-center justify-center h-full text-center text-muted-foreground">
        <MessageSquare className="h-8 w-8 mb-2 opacity-50" />
        <p className="text-sm">No vendor linked to this response</p>
        <p className="text-xs mt-1">Cannot load vendor contacts</p>
      </div>
    );
  }

  // Delegate to VendorChat with the linked vendor's ID
  return (
    <VendorChat
      vendorId={data.response.vendor.semantic_id}
      projectId={projectId}
    />
  );
}
